import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';

export type RentalAssetStatus = 'active' | 'retired';
export type RentalBookingStatus = 'reserved' | 'picked_up' | 'returned' | 'cancelled';

export interface RentalAsset {
  id: string;
  code: string;
  name: string;
  category: string | null;
  description: string | null;
  quantity: number;
  dailyRate: string;
  weeklyRate: string | null;
  replacementValue: string | null;
  deposit: string;
  currency: string;
  condition: string | null;
  location: string | null;
  status: RentalAssetStatus;
  isKit: boolean;
  notes: string | null;
  propItemId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface KitComponentRow {
  id: string;
  assetId: string;
  quantity: number;
  asset: { id: string; code: string; name: string };
}

export function useKit(kitId: string | null) {
  return useQuery({
    queryKey: ['kit', kitId],
    queryFn: () => api.get<KitComponentRow[]>(`/api/rental-assets/${kitId}/kit`),
    enabled: !!kitId,
  });
}
export function useSaveKit(kitId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (components: { assetId: string; quantity: number }[]) =>
      api.put<KitComponentRow[]>(`/api/rental-assets/${kitId}/kit`, { components }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['kit', kitId] }); qc.invalidateQueries({ queryKey: ['rental-assets'] }); },
  });
}

export interface RentalBookingItem {
  id: string;
  assetId: string;
  quantity: number;
  dailyRate: string;
  lineTotal: string;
  conditionOut: string | null;
  conditionIn: string | null;
  asset: { id: string; code: string; name: string; currency: string };
}

export interface RentalBooking {
  id: string;
  code: string;
  customerName: string;
  startDate: string;
  endDate: string;
  status: RentalBookingStatus;
  pickedUpAt: string | null;
  returnedAt: string | null;
  depositTaken: string;
  totalPrice: string;
  currency: string;
  note: string | null;
  client: { id: string; name: string } | null;
  items: RentalBookingItem[];
  createdAt: string;
}

export interface AvailabilityRow {
  id: string;
  code: string;
  name: string;
  category: string | null;
  quantity: number;
  dailyRate: number;
  weeklyRate: number | null;
  deposit: number;
  currency: string;
  available: number;
}
export interface Availability {
  days: number;
  assets: AvailabilityRow[];
}

// --- Assets ---
export function useRentalAssets(includeRetired = false) {
  return useQuery({
    queryKey: ['rental-assets', { includeRetired }],
    queryFn: () => api.get<RentalAsset[]>(`/api/rental-assets${includeRetired ? '?includeRetired=true' : ''}`),
  });
}
function useAssetMutation<T, R = unknown>(fn: (v: T) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: ['rental-assets'] }) });
}
export const useCreateRentalAsset = () => useAssetMutation((p: unknown) => api.post<RentalAsset>('/api/rental-assets', p));
export const useCreateAssetFromProp = () => useAssetMutation((p: { propItemId: string; dailyRate: number }) => api.post<RentalAsset>('/api/rental-assets/from-prop', p));
export const useUpdateRentalAsset = () => useAssetMutation(({ id, payload }: { id: string; payload: unknown }) => api.patch<RentalAsset>(`/api/rental-assets/${id}`, payload));
export const useDeleteRentalAsset = () => useAssetMutation((id: string) => api.del(`/api/rental-assets/${id}`));

// --- Bookings ---
export function useRentalBookings() {
  return useQuery({ queryKey: ['rental-bookings'], queryFn: () => api.get<RentalBooking[]>('/api/rental-bookings') });
}
export function useAvailability(startDate: string, endDate: string, enabled: boolean) {
  return useQuery({
    queryKey: ['rental-availability', startDate, endDate],
    queryFn: () => api.get<Availability>(`/api/rental-bookings/availability?startDate=${startDate}&endDate=${endDate}`),
    enabled,
  });
}
function useBookingMutation<T, R = unknown>(fn: (v: T) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rental-bookings'] });
      qc.invalidateQueries({ queryKey: ['rental-availability'] });
    },
  });
}
export const useCreateRentalBooking = () => useBookingMutation((p: unknown) => api.post<RentalBooking>('/api/rental-bookings', p));
export const useCreateBookingInvoice = () => useBookingMutation((id: string) => api.post<{ id: string; number: string }>(`/api/rental-bookings/${id}/invoice`, {}));
export const useSetBookingStatus = () => useBookingMutation(({ id, status }: { id: string; status: RentalBookingStatus }) => api.post<RentalBooking>(`/api/rental-bookings/${id}/status`, { status }));
export const useDeleteRentalBooking = () => useBookingMutation((id: string) => api.del(`/api/rental-bookings/${id}`));
export const useEnsureBookingQr = () => useBookingMutation((id: string) => api.post<{ token: string; url: string }>(`/api/rental-bookings/${id}/qr`, {}));

export const rentalPdfUrl = (id: string) => `/api/rental-bookings/${id}/pdf`;

// --- Mobilní výdej/vrácení přes QR (token) ---
export type RentalPhotoKind = 'pickup' | 'returned';
export interface PublicBookingItem {
  id: string;
  code: string;
  name: string;
  quantity: number;
  damageNote: string | null;
  units: { id: string; label: string }[];
}
export interface PublicBooking {
  code: string;
  customerName: string;
  startDate: string;
  endDate: string;
  status: RentalBookingStatus;
  pickedUpAt: string | null;
  returnedAt: string | null;
  note: string | null;
  items: PublicBookingItem[];
  photos: { id: string; kind: RentalPhotoKind; createdAt: string }[];
  hasPickupSignature: boolean;
  hasReturnSignature: boolean;
}
export function usePublicBooking(token: string) {
  return useQuery({ queryKey: ['booking-public', token], queryFn: () => api.get<PublicBooking>(`/api/portal/booking/${token}`), retry: false });
}
export function useBookingAction(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: 'pickup' | 'return') => api.post<PublicBooking>(`/api/rental-bookings/by-token/${token}/status`, { action }),
    onSuccess: (data) => qc.setQueryData(['booking-public', token], data),
  });
}

export const bookingPhotoUrl = (token: string, photoId: string, thumb = true) =>
  `/api/portal/booking/${token}/photo/${photoId}${thumb ? '?thumb=1' : ''}`;

// Upload fotky foto-protokolu (multipart) — fetch napřímo kvůli FormData.
export function useAddBookingPhoto(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, kind }: { file: File; kind: RentalPhotoKind }) => {
      const fd = new FormData();
      fd.append('kind', kind);
      fd.append('file', file);
      const res = await fetch(`/api/rental-bookings/by-token/${token}/photos`, { method: 'POST', body: fd, credentials: 'include' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new ApiError(res.status, (body as { message?: string }).message ?? `HTTP ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['booking-public', token] }),
  });
}

export const signatureUrl = (token: string, kind: RentalPhotoKind) => `/api/portal/booking/${token}/signature/${kind}`;

export function useSaveSignature(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { kind: RentalPhotoKind; dataUrl: string }) =>
      api.post<PublicBooking>(`/api/rental-bookings/by-token/${token}/signature`, p),
    onSuccess: (data) => qc.setQueryData(['booking-public', token], data),
  });
}

export function useReportDamage(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { itemId: string; unitId?: string; description: string }) =>
      api.post<PublicBooking>(`/api/rental-bookings/by-token/${token}/damage`, p),
    onSuccess: (data) => qc.setQueryData(['booking-public', token], data),
  });
}

// --- Analytika vytížení ---
export interface AnalyticsRow {
  id: string;
  code: string;
  name: string;
  category: string | null;
  currency: string;
  status: RentalAssetStatus;
  quantity: number;
  revenue: number;
  bookings: number;
  rentedUnitDays: number;
  utilizationPct: number;
  replacementValue: number | null;
  roiPct: number | null;
  idle: boolean;
}
export interface RentalAnalytics {
  windowDays: number;
  from: string;
  to: string;
  totals: { totalRevenue: number; avgUtilization: number; idleCount: number; activeAssets: number };
  rows: AnalyticsRow[];
}
export function useRentalAnalytics(days: number) {
  return useQuery({
    queryKey: ['rental-analytics', days],
    queryFn: () => api.get<RentalAnalytics>(`/api/rental-analytics?days=${days}`),
  });
}

// --- Veřejný katalog + poptávky ---
export interface PublicCatalogAsset {
  code: string;
  name: string;
  category: string | null;
  description: string | null;
  quantity: number;
  dailyRate: number;
  weeklyRate: number | null;
  deposit: number;
  currency: string;
}
export interface PublicCatalog {
  studioName: string;
  studioEmail: string | null;
  studioPhone: string | null;
  assets: PublicCatalogAsset[];
  generatedAt: string;
}

export function usePublicCatalog() {
  return useQuery({
    queryKey: ['rental-catalog'],
    queryFn: () => api.get<PublicCatalog>('/api/portal/rental-catalog'),
    retry: false,
  });
}

export interface InquiryInput {
  customerName: string;
  email?: string;
  phone?: string;
  message?: string;
  startDate?: string;
  endDate?: string;
  assetCodes?: string[];
  website?: string; // honeypot
}
export function useSubmitInquiry() {
  return useMutation({ mutationFn: (data: InquiryInput) => api.post<{ ok: boolean }>('/api/portal/rental-inquiry', data) });
}

export type RentalInquiryStatus = 'new' | 'handled';
export interface RentalInquiry {
  id: string;
  customerName: string;
  email: string | null;
  phone: string | null;
  message: string | null;
  startDate: string | null;
  endDate: string | null;
  assetCodes: string[] | null;
  status: RentalInquiryStatus;
  note: string | null;
  createdAt: string;
}

export function useRentalInquiries() {
  return useQuery({ queryKey: ['rental-inquiries'], queryFn: () => api.get<RentalInquiry[]>('/api/rental-inquiries') });
}
function useInquiryMutation<T>(fn: (v: T) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: ['rental-inquiries'] }) });
}
export const useSetInquiryStatus = () => useInquiryMutation(({ id, status, note }: { id: string; status: RentalInquiryStatus; note?: string }) => api.patch<RentalInquiry>(`/api/rental-inquiries/${id}`, { status, note }));
export const useDeleteInquiry = () => useInquiryMutation((id: string) => api.del(`/api/rental-inquiries/${id}`));

export const BOOKING_STATUS_META: Record<RentalBookingStatus, { label: string; badge: string }> = {
  reserved: { label: 'Rezervováno', badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
  picked_up: { label: 'Vydáno', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  returned: { label: 'Vráceno', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  cancelled: { label: 'Zrušeno', badge: 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400 line-through' },
};
